import { useEffect, useRef } from "react";
import { avatarHealthMonitor } from "../services/avatar-health-monitor.service";
import { WINDOW_EVENTS } from "../common/constants/app.constants";
import { toast, dismissToast } from "../components/shared/ToastNotification";
import { createStateUpdater } from "../common/utils/state";
import { HEALTH_MONITOR_EVENTS } from "../common/constants/healthCheck.constants";

interface UseEventListenersProps {
  addMessage: (message: any) => void;
  setSessionState: (state: any) => void;
  handleDisconnect: () => void | Promise<void>;
}

/**
 * Hook for handling event listeners
 * @param addMessage - Function to add a message to the app state
 * @param setSessionState - Function to update the session state
 * @param handleDisconnect - Function to handle disconnection
 */

export const useEventListeners = ({
  addMessage,
  setSessionState,
  handleDisconnect,
}: UseEventListenersProps) => {
  const updateSessionState = createStateUpdater(setSessionState);
  const idleWarningToastIdRef = useRef<string | number | null>(null);

  useEffect(() => {
    const handleAvatarReady = () => {
      updateSessionState({ avatarReady: true });
    };

    // Shared handler for browser-related errors (microphone, recording)
    const handleBrowserError = (event: any) => {
      toast({
        title: event.detail.error,
        variant: "error",
      });
      avatarHealthMonitor.recordCrash(event.detail.error, "browser");
    };

    const handleAvatarError = (event: any) => {
      const { error, type } = event.detail;

      const message = error || `Avatar error: ${type || "Unknown error"}`;

      toast({
        title: message,
        variant: "error",
      });
      updateSessionState({ avatarReady: false });
      avatarHealthMonitor.recordCrash(message, "avatar_service");
      handleDisconnect();
    };

    const handleConnectionFatalError = (event: any) => {
      const { message, errorType } = event.detail;

      toast({
        title: `Connection failed (${errorType}): ${message}`,
        variant: "error",
      });
      avatarHealthMonitor.recordCrash(message, "network");
      handleDisconnect();
    };

    const handleAvatarIdleWarning = (event: any) => {
      const seconds = Math.ceil(event?.detail?.secondsRemaining ?? 10);
      const minutes = seconds / 60;
      const timeLeft =
        minutes >= 1
          ? `${Math.floor(minutes)} minute${Math.floor(minutes) > 1 ? "s" : ""}`
          : `${seconds} second${seconds > 1 ? "s" : ""}`;
      const toastId = toast({
        title: `Your session will expire in ${timeLeft} due to inactivity.`,
        description: `Would you like to extend your session?`,
        variant: "warning",
        action: {
          label: "Extend Session",
          onClick: () => {
            avatarHealthMonitor.updateActivity();
          },
        },
        duration: seconds * 1000,
        closeButton: false,
        hasDurationCountdown: true,
      });
      idleWarningToastIdRef.current = toastId;
    };

    const handleAvatarActivity = () => {
      if (idleWarningToastIdRef.current !== null) {
        dismissToast(idleWarningToastIdRef.current);
        idleWarningToastIdRef.current = null;
      }
    };

    // Add event listeners
    window.addEventListener(WINDOW_EVENTS.AVATAR_READY, handleAvatarReady);
    window.addEventListener(WINDOW_EVENTS.MICROPHONE_ERROR, handleBrowserError);
    window.addEventListener(WINDOW_EVENTS.RECORDING_ERROR, handleBrowserError);
    window.addEventListener(
      HEALTH_MONITOR_EVENTS.AVATAR_ERROR,
      handleAvatarError
    );
    window.addEventListener(
      WINDOW_EVENTS.CONNECTION_FATAL_ERROR,
      handleConnectionFatalError
    );
    window.addEventListener(
      HEALTH_MONITOR_EVENTS.AVATAR_IDLE_WARNING,
      handleAvatarIdleWarning
    );
    window.addEventListener(
      HEALTH_MONITOR_EVENTS.AVATAR_ACTIVITY,
      handleAvatarActivity
    );

    // Cleanup function
    return () => {
      window.removeEventListener(WINDOW_EVENTS.AVATAR_READY, handleAvatarReady);
      window.removeEventListener(WINDOW_EVENTS.MICROPHONE_ERROR, handleBrowserError);
      window.removeEventListener(WINDOW_EVENTS.RECORDING_ERROR, handleBrowserError);
      window.removeEventListener(
        HEALTH_MONITOR_EVENTS.AVATAR_ERROR,
        handleAvatarError
      );
      window.removeEventListener(
        WINDOW_EVENTS.CONNECTION_FATAL_ERROR,
        handleConnectionFatalError
      );
      window.removeEventListener(
        HEALTH_MONITOR_EVENTS.AVATAR_IDLE_WARNING,
        handleAvatarIdleWarning
      );
      window.removeEventListener(
        HEALTH_MONITOR_EVENTS.AVATAR_ACTIVITY,
        handleAvatarActivity
      );
    };
  }, [addMessage, updateSessionState, handleDisconnect]);
};
